import { produce } from 'immer'
import { Dispatch, SetStateAction, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Divider, FormGroup, Icon, InputGroup } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import zipCloud from '../../domain/api/zipCloud'
import { ZipSearchResultEntity } from '../../domain/entities/zipCloud'
import styles from './ProfileDetail.module.scss'

type Contact = {
    use: string
    email: string
    phone: string
    url: string
    address: {
        zip: string
        country: string
        state: string
        city: string
        street: string
    }
}

export default ({
    idSuffix, inEditMode, reverseFields, contact, setContact
}: {
    idSuffix: string | number
    inEditMode: boolean
    reverseFields: boolean
    contact: Contact
    setContact: Dispatch<SetStateAction<Contact>>
}) => {
    const { t } = useTranslation()

    // after zip code is changed, the street input should be focused
    const streetInputRef = useRef<HTMLInputElement>(null)

    const isZipSearchSuccessResult =
        (data?: any): data is ZipSearchResultEntity =>
            data && typeof data === 'object' && data.status === 200

    zipCloud.search.useFetchSWR(contact.address.zip, {
        revalidateOnMount: true,
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        onSuccess(data): void {
            if (!isZipSearchSuccessResult(data)) return
            setContact(prev => produce(prev, draft => {
                if (draft.address.state === data.address1 &&
                    draft.address.city === data.address2 &&
                    draft.address.street.startsWith(data.address3)) return

                draft.address.zip = contact.address.zip
                draft.address.country = "日本"
                draft.address.state = data.address1
                draft.address.city = data.address2
                draft.address.street = data.address3
                streetInputRef.current?.focus()
            }))
        },
    })

    return <>
        <Divider className={styles.divider} />
        <FormGroup
            className={styles.formGroup}
            label={t('profileDetail.form.emailInput.label')}
            labelFor={`email-${idSuffix}`}>
            {inEditMode ? <>
                <InputGroup
                    id={`email-${idSuffix}`}
                    className={styles.input}
                    type="email"
                    autoComplete="email"
                    tabIndex={31}
                    leftIcon={IconNames.SendMessage}
                    placeholder="placeholder@email.com"
                    value={contact.email}
                    onChange={e => setContact(prev => produce(prev, draft => {
                        draft.email = e.target.value
                    }))}
                    readOnly={!inEditMode} />
            </> : <>
                {/* to avoid the warning: Incorrect use of labelFor and input id pair. */}
                <input id={`email-${idSuffix}`} className={styles.dummyInput} />
                <p className={styles.linkGroup}>
                    <Icon className={styles.icon} icon={IconNames.SendMessage} />
                    <Link to={`mailto:${contact.email}`}>{contact.email}</Link>
                </p>
            </>}
        </FormGroup>
        <FormGroup
            className={styles.formGroup}
            label={t('profileDetail.form.phoneInput.label')}
            labelFor={`phone-${idSuffix}`}>
            {inEditMode ? <>
                <InputGroup
                    id={`phone-${idSuffix}`}
                    className={styles.input}
                    type="tel"
                    autoComplete="tel"
                    tabIndex={32}
                    leftIcon={IconNames.Phone}
                    placeholder="000-0000-0000"
                    value={contact.phone}
                    onChange={e => setContact(prev => produce(prev, draft => {
                        draft.phone = e.target.value
                    }))}
                    readOnly={!inEditMode} />
            </> : <>
                {/* to avoid the warning: Incorrect use of labelFor and input id pair. */}
                <input id={`phone-${idSuffix}`} className={styles.dummyInput} />
                <p className={styles.linkGroup}>
                    <Icon className={styles.icon} icon={IconNames.Phone} />
                    <Link to={`tel:${contact.phone}`}>{contact.phone}</Link>
                </p>
            </>}
        </FormGroup>
        <FormGroup
            className={styles.formGroup}
            label={t('profileDetail.form.urlInput.label')}
            labelFor={`url-${idSuffix}`}>
            {inEditMode ? <>
                <InputGroup
                    id={`url-${idSuffix}`}
                    className={styles.input}
                    type="url"
                    autoComplete="url"
                    tabIndex={33}
                    leftIcon={IconNames.Link}
                    placeholder="https://"
                    value={contact.url}
                    onChange={e => setContact(prev => produce(prev, draft => {
                        draft.url = e.target.value
                    }))}
                    readOnly={!inEditMode} />
            </> : <>
                {/* to avoid the warning: Incorrect use of labelFor and input id pair. */}
                <input id={`url-${idSuffix}`} className={styles.dummyInput} />
                <p className={styles.linkGroup}>
                    <Icon className={styles.icon} icon={IconNames.Link} />
                    <Link
                        to={contact.url}
                        target="_blank"
                        rel="noopener noreferrer">
                        {contact.url}
                    </Link>
                </p>
            </>}
        </FormGroup>
        <div className={`${styles.verticalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
            <FormGroup
                className={styles.formGroup}
                label={t('profileDetail.form.streetInput.label')}
                labelFor={`street-${idSuffix}`}>
                <InputGroup
                    id={`street-${idSuffix}`}
                    className={styles.input}
                    inputRef={streetInputRef}
                    type="text"
                    autoComplete="street-address"
                    tabIndex={reverseFields ? 38 : 34}
                    leftIcon={IconNames.Map}
                    value={contact.address.street}
                    onChange={e => setContact(prev => produce(prev, draft => {
                        draft.address.street = e.target.value
                    }))}
                    readOnly={!inEditMode} />
            </FormGroup>
            <div className={`${styles.horizontalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.cityInput.label')}
                    labelFor={`city-${idSuffix}`}>
                    <InputGroup
                        id={`city-${idSuffix}`}
                        className={styles.input}
                        type="text"
                        autoComplete="address-level2"
                        tabIndex={reverseFields ? 37 : 35}
                        leftIcon={IconNames.MapMarker}
                        value={contact.address.city}
                        onChange={e => setContact(prev => produce(prev, draft => {
                            draft.address.city = e.target.value
                        }))}
                        readOnly={!inEditMode} />
                </FormGroup>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.stateInput.label')}
                    labelFor={`state-${idSuffix}`}>
                    <InputGroup
                        id={`state-${idSuffix}`}
                        className={styles.input}
                        type="text"
                        autoComplete="address-level1"
                        tabIndex={36}
                        leftIcon={IconNames.MapMarker}
                        value={contact.address.state}
                        onChange={e => setContact(prev => produce(prev, draft => {
                            draft.address.state = e.target.value
                        }))}
                        readOnly={!inEditMode} />
                </FormGroup>
            </div>
            <div className={`${styles.horizontalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.countryInput.label')}
                    labelFor={`country-${idSuffix}`}>
                    <InputGroup
                        id={`country-${idSuffix}`}
                        className={styles.input}
                        type="text"
                        autoComplete="country-name"
                        tabIndex={reverseFields ? 35 : 37}
                        leftIcon={IconNames.MapMarker}
                        value={contact.address.country}
                        onChange={e => setContact(prev => produce(prev, draft => {
                            draft.address.country = e.target.value
                        }))}
                        readOnly={!inEditMode} />
                </FormGroup>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.zipCodeInput.label')}
                    labelFor={`zip-${idSuffix}`}>
                    <InputGroup
                        id={`zip-${idSuffix}`}
                        className={styles.input}
                        type="text"
                        autoComplete="postal-code"
                        tabIndex={reverseFields ? 34 : 38}
                        leftIcon={IconNames.Locate}
                        placeholder="000-0000"
                        value={contact.address.zip}
                        onChange={e => setContact(prev => produce(prev, draft => {
                            draft.address.zip = e.target.value
                        }))}
                        readOnly={!inEditMode} />
                </FormGroup>
            </div>
        </div>
    </>
}